import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileText, Receipt, HardHat, FolderOpen, Ticket, AlertTriangle,
  CheckCircle, Clock, Calendar, Euro, Download, ExternalLink,
  User, MapPin, Phone, Mail, Plus, X, MessageSquare, Image,
  ChevronRight, Home
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast, Toaster } from 'sonner';

import { API_URL } from '../../config/api';

const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/4e491698-f834-49a8-a1e5-8fd3efa88feb/images/d77c0364065b8ec483b9e6915ff1d30475de2851aefe5560f16a14318cc351be.png";

const chantierStatusLabels = {
  to_plan: { label: 'À planifier', color: 'bg-slate-100 text-slate-700' },
  planned: { label: 'Planifié', color: 'bg-blue-100 text-blue-700' },
  in_progress: { label: 'En cours', color: 'bg-yellow-100 text-yellow-700' },
  completed: { label: 'Terminé', color: 'bg-green-100 text-green-700' },
  on_hold: { label: 'En pause', color: 'bg-orange-100 text-orange-700' }
};

const invoiceStatusLabels = {
  draft: { label: 'Brouillon', color: 'bg-slate-100 text-slate-700' },
  sent: { label: 'Envoyée', color: 'bg-blue-100 text-blue-700' },
  paid: { label: 'Payée', color: 'bg-green-100 text-green-700' },
  overdue: { label: 'En retard', color: 'bg-red-100 text-red-700' },
  cancelled: { label: 'Annulée', color: 'bg-slate-100 text-slate-700' }
};

const ticketTypeLabels = {
  malfacon: 'Malfaçon',
  retard: 'Retard',
  facturation: 'Facturation',
  garantie: 'Garantie',
  reclamation: 'Réclamation',
  autre: 'Autre'
};

export default function ClientPortalPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState('chantiers');
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [newTicket, setNewTicket] = useState({
    subject: '',
    description: '',
    ticket_type: 'reclamation',
    chantier_id: ''
  });

  useEffect(() => {
    fetchPortalData();
  }, [token]);

  const fetchPortalData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/batiment/portal/${token}`);
      if (!response.ok) {
        if (response.status === 404) {
          setError('Ce lien n\'est plus valide ou a expiré.');
        } else {
          setError('Une erreur est survenue.');
        }
        return;
      }
      const result = await response.json();
      setData(result);
    } catch (err) {
      setError('Impossible de charger les données.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    if (!newTicket.subject || !newTicket.description) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    try {
      const params = new URLSearchParams({
        subject: newTicket.subject,
        description: newTicket.description,
        ticket_type: newTicket.ticket_type
      });
      if (newTicket.chantier_id) {
        params.append('chantier_id', newTicket.chantier_id);
      }

      const response = await fetch(`${API_URL}/api/batiment/portal/${token}/tickets?${params.toString()}`, {
        method: 'POST'
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`Ticket ${result.ticket_number} créé avec succès`);
        setShowTicketModal(false);
        setNewTicket({ subject: '', description: '', ticket_type: 'reclamation', chantier_id: '' });
        fetchPortalData(); // Refresh data
      } else {
        toast.error('Erreur lors de la création du ticket');
      }
    } catch (err) {
      toast.error('Erreur lors de la création du ticket');
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-rm-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center">
          <AlertTriangle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-rm-text mb-2">Accès impossible</h1>
          <p className="text-rm-text-secondary">{error}</p>
          <p className="text-sm text-rm-text-secondary mt-4">
            Contacte ton entrepreneur pour obtenir un nouveau lien d'accès.
          </p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'chantiers', label: 'Mes Chantiers', icon: HardHat, count: data?.chantiers?.length },
    { id: 'devis', label: 'Devis', icon: FileText, count: data?.quotes?.length },
    { id: 'factures', label: 'Factures', icon: Receipt, count: data?.invoices?.length },
    { id: 'documents', label: 'Documents', icon: FolderOpen, count: data?.documents?.length },
    { id: 'tickets', label: 'Mes demandes', icon: Ticket, count: data?.tickets?.length }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100" data-testid="client-portal-page">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img src={LOGO_URL} alt="Logo" className="h-10 object-contain" />
              <div>
                <h1 className="text-lg font-bold text-rm-text">Mon Espace Chantier</h1>
                <p className="text-sm text-rm-text-secondary">Bienvenue, {data?.client?.name}</p>
              </div>
            </div>
            <Button 
              onClick={() => setShowTicketModal(true)} 
              className="bg-rm-primary hover:bg-rm-primary/90"
              data-testid="open-ticket-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Signaler un problème
            </Button>
          </div>
        </div>
      </header>

      {/* Client Info Banner */}
      <div className="bg-rm-primary text-white">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex flex-wrap gap-6 text-sm">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 opacity-70" />
              <span>{data?.client?.name}</span>
            </div>
            {data?.client?.email && (
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 opacity-70" />
                <span>{data?.client?.email}</span>
              </div>
            )}
            {data?.client?.phone && (
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 opacity-70" />
                <span>{data?.client?.phone}</span>
              </div>
            )}
            {data?.client?.address && (
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 opacity-70" />
                <span>{data?.client?.address}, {data?.client?.city}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-rm-primary text-white'
                  : 'bg-white text-rm-text-secondary hover:bg-slate-100'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.count > 0 && (
                <span className={`px-2 py-0.5 rounded-full text-xs ${
                  activeTab === tab.id ? 'bg-white/20' : 'bg-slate-100'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          {/* Chantiers Tab */}
          {activeTab === 'chantiers' && (
            <div className="divide-y divide-slate-100">
              {data?.chantiers?.length === 0 ? (
                <div className="text-center py-12">
                  <HardHat className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-rm-text-secondary">Aucun chantier en cours</p>
                </div>
              ) : (
                data?.chantiers?.map((chantier) => (
                  <div key={chantier.id} className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-semibold text-rm-text text-lg">{chantier.address}</h3>
                        <p className="text-sm text-rm-text-secondary">{chantier.work_description}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        chantierStatusLabels[chantier.status]?.color || 'bg-slate-100'
                      }`}>
                        {chantierStatusLabels[chantier.status]?.label || chantier.status}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-rm-text-secondary">Devis</span>
                        <p className="font-medium">{chantier.quote_number}</p>
                      </div>
                      <div>
                        <span className="text-rm-text-secondary">Montant</span>
                        <p className="font-medium">{formatCurrency(chantier.quote_amount)}</p>
                      </div>
                      {chantier.start_date && (
                        <div>
                          <span className="text-rm-text-secondary">Début prévu</span>
                          <p className="font-medium">{formatDate(chantier.start_date)}</p>
                        </div>
                      )}
                      {chantier.end_date && (
                        <div>
                          <span className="text-rm-text-secondary">Fin prévue</span>
                          <p className="font-medium">{formatDate(chantier.end_date)}</p>
                        </div>
                      )}
                    </div>

                    {/* Progress bar */}
                    {chantier.status === 'in_progress' && (
                      <div className="mt-4">
                        <div className="flex justify-between text-xs text-rm-text-secondary mb-1">
                          <span>Progression</span>
                          <span>{chantier.progress || 0}%</span>
                        </div>
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-rm-primary rounded-full transition-all"
                            style={{ width: `${chantier.progress || 0}%` }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Comments/Notes */}
                    {chantier.comments?.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-slate-100">
                        <h4 className="text-sm font-medium text-rm-text mb-2 flex items-center gap-2">
                          <MessageSquare className="w-4 h-4" />
                          Notes de l'entrepreneur
                        </h4>
                        <div className="space-y-2">
                          {chantier.comments.slice(0, 3).map((comment, i) => (
                            <div key={i} className="bg-slate-50 rounded-lg p-3 text-sm">
                              <p className="text-rm-text-secondary">{comment.content}</p>
                              <p className="text-xs text-slate-400 mt-1">{formatDate(comment.created_at)}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {/* Devis Tab */}
          {activeTab === 'devis' && (
            <div className="divide-y divide-slate-100">
              {data?.quotes?.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-rm-text-secondary">Aucun devis</p>
                </div>
              ) : (
                data?.quotes?.map((quote) => (
                  <div key={quote.id} className="p-6 hover:bg-slate-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-mono text-sm text-rm-text-secondary">{quote.quote_number}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs ${
                            quote.status === 'accepted' ? 'bg-green-100 text-green-700' :
                            quote.status === 'sent' ? 'bg-blue-100 text-blue-700' :
                            quote.status === 'refused' ? 'bg-red-100 text-red-700' :
                            'bg-slate-100 text-slate-700'
                          }`}>
                            {quote.status === 'accepted' ? 'Accepté' :
                             quote.status === 'sent' ? 'En attente' :
                             quote.status === 'refused' ? 'Refusé' :
                             quote.status === 'draft' ? 'Brouillon' : quote.status}
                          </span>
                        </div>
                        <p className="text-rm-text font-medium">{quote.work_description}</p>
                        <p className="text-sm text-rm-text-secondary mt-1">
                          Créé le {formatDate(quote.created_at)}
                          {quote.valid_until && ` • Valide jusqu'au ${formatDate(quote.valid_until)}`}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-rm-text">{formatCurrency(quote.total)}</p>
                        <p className="text-xs text-rm-text-secondary">TTC</p>
                      </div>
                    </div>
                    
                    {quote.status === 'sent' && quote.signature_link && (
                      <div className="mt-4">
                        <a
                          href={quote.signature_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-rm-primary text-white rounded-lg hover:bg-rm-primary/90 transition-colors"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Signer ce devis
                        </a>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {/* Factures Tab */}
          {activeTab === 'factures' && (
            <div className="divide-y divide-slate-100">
              {data?.invoices?.length === 0 ? (
                <div className="text-center py-12">
                  <Receipt className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-rm-text-secondary">Aucune facture</p>
                </div>
              ) : (
                data?.invoices?.map((invoice) => (
                  <div key={invoice.id} className="p-6 hover:bg-slate-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-mono text-sm text-rm-text-secondary">{invoice.invoice_number}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs ${
                            invoiceStatusLabels[invoice.status]?.color || 'bg-slate-100'
                          }`}>
                            {invoiceStatusLabels[invoice.status]?.label || invoice.status}
                          </span>
                        </div>
                        <p className="text-rm-text font-medium">{invoice.work_description}</p>
                        <p className="text-sm text-rm-text-secondary mt-1">
                          Émise le {formatDate(invoice.created_at)}
                          {invoice.due_date && ` • Échéance le ${formatDate(invoice.due_date)}`}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-rm-text">{formatCurrency(invoice.total)}</p>
                        <p className="text-xs text-rm-text-secondary">TTC</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Documents Tab */}
          {activeTab === 'documents' && (
            <div className="p-6">
              {data?.documents?.length === 0 ? (
                <div className="text-center py-12">
                  <FolderOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-rm-text-secondary">Aucun document</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {data?.documents?.map((doc) => (
                    <a
                      key={doc.id}
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-slate-50 rounded-lg p-4 hover:bg-slate-100 transition-colors group"
                    >
                      <div className="aspect-square bg-white rounded-lg mb-3 flex items-center justify-center overflow-hidden">
                        {doc.type === 'photo' ? (
                          <img src={doc.url} alt={doc.name} className="w-full h-full object-cover" />
                        ) : (
                          <FileText className="w-12 h-12 text-slate-300" />
                        )}
                      </div>
                      <p className="text-sm font-medium text-rm-text truncate">{doc.name}</p>
                      <p className="text-xs text-rm-text-secondary">{formatDate(doc.created_at)}</p>
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Tickets Tab */}
          {activeTab === 'tickets' && (
            <div className="divide-y divide-slate-100">
              {data?.tickets?.length === 0 ? (
                <div className="text-center py-12">
                  <Ticket className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-rm-text-secondary">Aucune demande</p>
                  <Button 
                    onClick={() => setShowTicketModal(true)} 
                    className="mt-4 bg-rm-primary"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Signaler un problème
                  </Button>
                </div>
              ) : (
                data?.tickets?.map((ticket) => (
                  <div key={ticket.id} className="p-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono text-xs text-rm-text-secondary">{ticket.ticket_number}</span>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                            {ticketTypeLabels[ticket.ticket_type] || ticket.ticket_type}
                          </span>
                        </div>
                        <h3 className="font-medium text-rm-text">{ticket.subject}</h3>
                        <p className="text-sm text-rm-text-secondary mt-1">{ticket.description}</p>
                        <p className="text-xs text-rm-text-secondary mt-2">
                          Créé le {formatDate(ticket.created_at)}
                        </p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        ticket.status === 'new' ? 'bg-blue-100 text-blue-700' :
                        ticket.status === 'in_progress' ? 'bg-yellow-100 text-yellow-700' :
                        ticket.status === 'resolved' ? 'bg-green-100 text-green-700' :
                        ticket.status === 'closed' ? 'bg-slate-100 text-slate-700' :
                        'bg-purple-100 text-purple-700'
                      }`}>
                        {ticket.status === 'new' ? 'Nouveau' :
                         ticket.status === 'in_progress' ? 'En cours' :
                         ticket.status === 'waiting' ? 'En attente' :
                         ticket.status === 'resolved' ? 'Résolu' :
                         ticket.status === 'closed' ? 'Fermé' : ticket.status}
                      </span>
                    </div>

                    {ticket.resolution && (
                      <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-3">
                        <p className="text-sm text-green-700">{ticket.resolution}</p>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-100 mt-12">
        <div className="max-w-6xl mx-auto px-4 py-6 text-center text-sm text-rm-text-secondary">
          <p>Portail client propulsé par <strong>BatiAssist</strong></p>
        </div>
      </footer>

      {/* Create Ticket Modal */}
      <AnimatePresence>
        {showTicketModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowTicketModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-lg shadow-xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-rm-text">Signaler un problème</h2>
                  <button onClick={() => setShowTicketModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <form onSubmit={handleCreateTicket} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-rm-text mb-1">Type de problème</label>
                  <select
                    value={newTicket.ticket_type}
                    onChange={(e) => setNewTicket({ ...newTicket, ticket_type: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg"
                  >
                    <option value="malfacon">Malfaçon / Défaut</option>
                    <option value="retard">Retard</option>
                    <option value="facturation">Problème de facturation</option>
                    <option value="garantie">Demande de garantie</option>
                    <option value="reclamation">Réclamation générale</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>

                {data?.chantiers?.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-rm-text mb-1">Chantier concerné</label>
                    <select
                      value={newTicket.chantier_id}
                      onChange={(e) => setNewTicket({ ...newTicket, chantier_id: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg"
                    >
                      <option value="">-- Sélectionner --</option>
                      {data.chantiers.map(c => (
                        <option key={c.id} value={c.id}>{c.address}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-rm-text mb-1">Sujet *</label>
                  <input
                    type="text"
                    required
                    value={newTicket.subject}
                    onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg"
                    placeholder="Résumé du problème"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-rm-text mb-1">Description *</label>
                  <textarea
                    required
                    value={newTicket.description}
                    onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg"
                    rows={4}
                    placeholder="Décrivez le problème en détail..."
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowTicketModal(false)}>
                    Annuler
                  </Button>
                  <Button type="submit" className="bg-rm-primary">
                    Envoyer
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
